import { FC, useMemo, PropsWithChildren, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, UnknownFunction } from '@eon-home/react-library';

import { SpotPriceType } from '@tools/types';
import { SpotPriceTrigger } from '@containers/emobility';
import { Infobox } from '@components';

import './index.scss';

interface Props {
    title: string;
    onSave: UnknownFunction;
    onCancel: UnknownFunction;
    isActive: boolean;
    infoboxContent?: ComponentProps<typeof Infobox>['children'];
    showActions?: boolean;
    showNext?: boolean;
    spotPriceType?: SpotPriceType;
    isSaveButtonDisabled?: boolean;
}

export const EmobilityChargingModeSettings: FC<
    Readonly<PropsWithChildren<Props>>
> = ({
    title,
    onSave,
    onCancel,
    children,
    isActive,
    infoboxContent,
    showActions = true,
    showNext = false,
    spotPriceType,
    isSaveButtonDisabled,
}) => {
    const { t } = useTranslation();
    const buttonLabel = useMemo(() => {
        return showNext ? t('Next') : isActive ? t('Save') : t('Activate');
    }, [showNext, isActive, t]);

    return (
        <div className="c-charging-mode__settings">
            <h3>{title}</h3>

            {infoboxContent ? (
                <Infobox useRoundedCorners={true}>{infoboxContent}</Infobox>
            ) : null}

            {typeof spotPriceType !== 'undefined' && (
                <SpotPriceTrigger type={spotPriceType} />
            )}

            <div className="c-charging-mode__settings-content">
                {children}

                {showActions ? (
                    <footer className="c-charging-mode__settings-actions">
                        <Button
                            tiny
                            outline
                            type="button"
                            onClick={() => onCancel()}
                            className="c-btn--turquoise"
                        >
                            {t('Cancel')}
                        </Button>

                        <Button
                            tiny
                            type="button"
                            onClick={() =>
                                isSaveButtonDisabled ? {} : onSave()
                            }
                            className={`c-btn--turquoise ${
                                isSaveButtonDisabled ? 'c-btn--disabled' : ''
                            }`}
                        >
                            {buttonLabel}
                        </Button>
                    </footer>
                ) : null}
            </div>
        </div>
    );
};

export default EmobilityChargingModeSettings;
